<template>
  <Modal
    ref="modal"
    :title="title"
  >
    <h2 class="text-subtitle1 q-mt-none justify-be">
      Date Range
    </h2>
    <SelectDateRange
      v-model="dateRange"
      :date-of-loss="client.dateOfLoss"
    />
    <q-separator class="q-mt-md" />
    <h2 class="text-subtitle1">
      Media
    </h2>
    <MediaFilter
      v-model="mediaTypes"
      rounded
    />
    <q-separator class="q-mt-md" />
    <h2 class="text-subtitle1">
      Social Sites
    </h2>
    <PostsPlatformToolbar
      :category-ids="categoryIds"
      :lifecycle-statuses="lifecycleStatuses"
      @change="onCategoryIdsChanged"
      @lifecycle-change="onLifecycleChange"
    />
    <q-separator class="q-mt-md" />
    <h2 class="text-subtitle1">
      Posts
    </h2>
    <SelectStatus
      :filters="postFilters"
      @update:filters="selectStatus"
    />

    <q-separator class="q-mt-md" />
    <h2 class="text-subtitle1">
      Filter by People
      <PersonsSidebarBtn
        :client-id="client.id"
        :person-ids="personIds"
        @update:person-ids="personIds = $event"
      />
    </h2>

    <q-separator class="q-my-md" />
    <q-input
      v-model="name"
      label="Report Name"
      outlined
    />

    <div class="q-mt-md text-center">
      <TotalPosts :filters="postFilters" />
    </div>

    <template v-slot:actions>
      <AsyncBtn
        label="Generate"
        color="primary"
        :task="task"
        :disable="!canGenerate"
        @click="generate"
      />
    </template>
  </Modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { ModalMixin } from '@/mixins';
import {
  PostPlatformCategoryFilter,
  Client,
  Report,
  OpenDateRange,
  PostFilters,
  Modal as ModalElement,
} from '@/type';
import PostsPlatformToolbar from '@/social/post/list/PostsPlatformToolbar.vue';
import Modal from '@/components/modal/Modal.vue';
import AsyncBtn from '@/components/AsyncBtn.vue';
import { name as clientName } from '@/client/client-filter';
import SelectStatus from '@/social/post/SelectStatus.vue';
import { status } from '@/social/post/post';
import notify from '@/components/notify';
import MediaFilter from '@/components/form/MediaFilter.vue';
import PersonsSidebarBtn from '@/analysis/person/PersonsSidebarBtn.vue';
import SelectDateRange from './SelectDateRange.vue';
import TotalPosts from './TotalPosts.vue';
import { generate } from '../report';
import { types as reportTypes } from '..';

interface Data {
  startDate?: string | null,
  endDate?: string | null,
  socialSiteIds: number[],
  statuses: number[],
  isFlagged?: boolean,
  isOnTimeline?: boolean,
  isInclusive: boolean,
  mediaTypes?: string[],
  lifecycleStatuses?: string[],
  personIds: number[],
  name: string,
  task: Promise<Report> | null,
  categoryIds: PostPlatformCategoryFilter,
}

export default defineComponent({
  emits: ['ok'],
  mixins: [ModalMixin],
  components: {
    Modal,
    SelectDateRange,
    SelectStatus,
    TotalPosts,
    AsyncBtn,
    MediaFilter,
    PostsPlatformToolbar,
    PersonsSidebarBtn,
  },
  props: {
    client: {
      type: Object as () => Client,
      required: true,
    },
  },
  data(): Data {
    return {
      startDate: null,
      endDate: null,
      socialSiteIds: [],
      categoryIds: {} as PostPlatformCategoryFilter,
      statuses: [
        status.UNREVIEWED,
        status.ESCALATED,
        status.RESOLVED,
      ],
      isFlagged: true,
      isOnTimeline: true,
      isInclusive: true,
      mediaTypes: [],
      lifecycleStatuses: undefined,
      personIds: [],
      name: '',
      task: null,
    };
  },
  computed: {
    title(): string {
      return `Report - ${clientName(this.client)}`;
    },
    dateRange: {
      get(): OpenDateRange {
        return {
          start: this.startDate,
          end: this.endDate,
        };
      },
      set(dateRange: OpenDateRange) {
        this.startDate = dateRange.start;
        this.endDate = dateRange.end;
      },
    },
    postFilters(): PostFilters {
      return {
        clientIds: [this.client.id],
        since: this.startDate,
        until: this.endDate,
        statuses: this.statuses,
        isOnTimeline: this.isOnTimeline,
        isFlagged: this.isFlagged,
        isInclusive: this.isInclusive,
        socialSiteIds: this.socialSiteIds,
        categoryIds: this.categoryIds,
        isTrashed: false,
        isPrivate: false,
        mediaTypes: this.mediaTypes,
        lifecycleStatuses: this.lifecycleStatuses,
        personIds: this.personIds,
      };
    },
    canGenerate(): boolean {
      return this.name !== '';
    },
  },
  methods: {
    onLifecycleChange(values: string[]) {
      this.lifecycleStatuses = values;
    },
    onCategoryIdsChanged(state: Record<string, any>) {
      this.categoryIds = ((state || {}).categoryIds || {});
    },
    selectStatus(filters: PostFilters) {
      this.statuses = filters.statuses;
      this.isFlagged = filters.isFlagged;
      this.isOnTimeline = filters.isOnTimeline;
      this.lifecycleStatuses = filters.lifecycleStatuses;
    },
    generate() {
      this.task = generate(
        this.client.id, reportTypes.LEGAL, this.name, this.statuses,
        this.isInclusive, this.socialSiteIds, this.isOnTimeline,
        this.isFlagged, this.mediaTypes, this.personIds, this.startDate, this.endDate,
        this.categoryIds,
      );
      this.task.then((report: Report) => {
        this.$emit('ok', report);
        (this.$refs.modal as ModalElement).hide();
        notify('Report generated.');
      });
    },
  },
});
</script>
